import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js'
import { convertHexToRgba } from '../../../../../utility/stylesUtility'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './MarketingAnalyticsWidget.module.scss'
import globals from '../../../../../assets/styles/globals.scss'
import { ReactComponent as StatsUpIcon } from '../../../../../assets/images/common/icons/stats-up-icon.svg'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const CHART_CONFIGS = {
  landingPageViews: {
    title: 'Landing Page Views',
    color: globals.primaryOrange,
  },
  leadsGenerated: {
    title: 'Leads Generated',
    color: globals.tertiaryBlueText,
  },
  registrations: {
    title: 'Registrations',
    color: globals.slateGray,
  },
}

const computeGrowthRate = (total, prevYearValue) => {
  if (prevYearValue === null || prevYearValue === undefined || prevYearValue === 0) {
    return null
  }
  return (total - prevYearValue) / prevYearValue
}

const formatGrowthRate = (rate) => {
  if (rate === null) {
    return 'N/A'
  }
  return Intl.NumberFormat('en-US', { style: 'percent', signDisplay: 'exceptZero' })
    .format(rate)
}

const MarketingAnalyticsLineChart = ({ chartKey, labels, data, statistics }) => {
  const config = CHART_CONFIGS[chartKey]
  const growthRate = computeGrowthRate(statistics?.value, statistics?.prevYearValue)

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      label: config.title,
      data,
      borderColor: config.color,
      backgroundColor: convertHexToRgba(config.color, 0.15),
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 2,
    }],
  }), [labels, data, config])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  }), [])

  return (
    <div className={classnames('d-flex flex-column', businessDashboardClasses.sectionWrapper, classes.sectionWrapper)}>
      <h3 className={businessDashboardClasses.title}>{config.title}</h3>
      <div className={classnames('d-flex align-items-end', classes.chartStatisticsWrapper)}>
        <span className={classes.chartValue}>{statistics?.value ?? '-'}</span>
        <StatsUpIcon className={classnames('ml-2 mb-1', classes.icon)} />
        {growthRate !== null && growthRate !== 0 && (
          <span
            className={classnames(
              'ml-4 mb-1',
              classes.growthValue,
              { [classes.green]: growthRate > 0 },
              { [classes.red]: growthRate < 0 }
            )}
          >
            {formatGrowthRate(growthRate)}
          </span>
        )}
      </div>
      <div className='flex-grow-1 min-w-0 min-h-0'>
        <Line data={chartData} options={options} />
      </div>
      <div className='d-flex align-items-center'>
        <span className={classes.todayValue}>{statistics?.todayValue ?? '-'}</span>
        <span className={classnames('ml-2', classes.todayLabel)}>Today</span>
      </div>
    </div>
  )
}

MarketingAnalyticsLineChart.propTypes = {
  chartKey: PropTypes.oneOf(['landingPageViews', 'leadsGenerated', 'registrations']).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  statistics: PropTypes.shape({
    value: PropTypes.number,
    todayValue: PropTypes.number,
    prevYearValue: PropTypes.number,
  }),
}

export default MarketingAnalyticsLineChart
